/**
 * The approve gate's settle rule (AIC-259, AIC-274; Bill's option B, 2026-09-29), shared by tracker-api
 * (results/concerns.ts undecidedBlocking) and the analyst portal (screens/resultFields.ts), so the two can't drift.
 * Pure and dependency-free: the portal bundles it.
 *
 * While approve_requires_concern_decisions is on, a blocking concern (a data concern) holds Approve until it is
 * settled: resolved, or accepted with its required note. No disposition, or follow_up (waiting on the vendor), is not
 * settled. A follow-up-only concern never holds Approve, whatever its disposition.
 */

export interface GateConcern {
  readonly blocking: boolean;
  readonly disposition: string | null;
  readonly note?: string | null;
}

/** Whether a disposition (with its note) settles a data concern for the approve gate. */
export const settlesBlocking = (c: Pick<GateConcern, "disposition" | "note">): boolean =>
  c.disposition === "resolved" || (c.disposition === "accepted" && !!c.note?.trim());

/** Whether the concern holds Approve while the gate is on: a data concern not yet settled. */
export const holdsApprove = (c: GateConcern): boolean => c.blocking && !settlesBlocking(c);

/** A data concern marked follow_up while the gate is on: waiting on the vendor, and holding Approve. */
export const waitsOnVendor = (c: GateConcern, gateOn: boolean): boolean =>
  gateOn && c.disposition === "follow_up" && holdsApprove(c);
